import { useEffect, useMemo, useState } from "react";
import { FiClock, FiMapPin, FiScissors, FiTag } from "react-icons/fi";
import Button from "../common/Button";
import formatCurrency from "../../utils/formatCurrency";
import {
  MOBILE_MODE_DESCRIPTION,
  STUDIO_MODE_DESCRIPTION,
} from "../../utils/constants";

const normalizeImages = (service) => {
  const gallery = Array.isArray(service?.serviceImages)
    ? service.serviceImages
    : [];

  if (gallery.length > 0) {
    return gallery
      .map((image, index) => ({
        id: image?.id ?? `image-${index}`,
        imageUrl: image?.imageUrl || "",
        displayOrder: image?.displayOrder ?? index,
      }))
      .filter((image) => image.imageUrl);
  }

  return service?.serviceImageUrl
    ? [{ id: "cover", imageUrl: service.serviceImageUrl, displayOrder: 0 }]
    : [];
};

const ServiceDetails = ({
  service,
  onBookNow,
  showBookingButton = true,
  className = "",
}) => {
  const images = useMemo(() => normalizeImages(service), [service]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [service?.id]);

  if (!service) {
    return (
      <div className={`content-card-lg p-6 ${className}`}>
        <p className="text-sm text-muted-warm">
          Service details are not available right now.
        </p>
      </div>
    );
  }

  const selectedImage = images[selectedIndex]?.imageUrl || "";

  return (
    <div className={`content-card-lg overflow-hidden ${className}`}>
      <div className="grid gap-6 p-3 md:gap-8 md:p-5 xl:grid-cols-[140px_minmax(0,1.25fr)_minmax(420px,0.95fr)] 2xl:grid-cols-[150px_minmax(0,1.3fr)_minmax(500px,1fr)]">
        <div className="order-2 flex gap-3 overflow-x-auto xl:order-1 xl:flex-col xl:overflow-visible">
          {images.length > 0 ? (
            images.map((image, index) => {
              const isActive = index === selectedIndex;

              return (
                <button
                  key={image.id}
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  className={`shrink-0 overflow-hidden rounded-2xl border bg-white transition xl:w-full ${
                    isActive
                      ? "border-henna ring-2 ring-henna/20"
                      : "border-goldlight hover:border-henna"
                  }`}
                >
                  <img
                    src={image.imageUrl}
                    alt={`${service?.serviceName || "Service"} ${index + 1}`}
                    className="h-24 w-24 object-cover sm:h-28 sm:w-28 xl:h-32 xl:w-full"
                  />
                </button>
              );
            })
          ) : (
            <div className="hero-overlay flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl text-white sm:h-28 sm:w-28 xl:h-32 xl:w-full">
              <FiScissors size={24} />
            </div>
          )}
        </div>

        <div className="order-1 overflow-hidden rounded-[30px] border border-goldlight bg-soft-cream xl:order-2">
          {selectedImage ? (
            <img
              src={selectedImage}
              alt={service?.serviceName || "Henna service"}
              className="h-[340px] w-full object-cover md:h-[500px] xl:h-[700px]"
            />
          ) : (
            <div className="hero-overlay flex h-[340px] items-center justify-center text-white md:h-[500px] xl:h-[700px]">
              <div className="text-center">
                <FiScissors size={46} className="mx-auto" />
                <p className="mt-4 text-sm font-semibold uppercase tracking-[0.2em]">
                  Nayra Henna Service
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="order-3 rounded-[30px] border border-goldlight bg-white p-6 md:p-8 xl:p-10">
          <div className="flex flex-col gap-6 xl:gap-7">
            <div>
              <h2 className="text-3xl font-semibold leading-tight text-henna md:text-4xl">
                {service?.serviceName || "Service"}
              </h2>

              <div className="mt-5 flex flex-wrap gap-3">
                {service?.serviceType ? (
                  <span className="badge-soft">
                    <FiTag size={14} />
                    {service.serviceType}
                  </span>
                ) : null}

                {service?.hennaType ? (
                  <span className="badge-soft">
                    <FiScissors size={14} />
                    {service.hennaType}
                  </span>
                ) : null}

                {service?.serviceDurationMinutes ? (
                  <span className="badge-soft">
                    <FiClock size={14} />
                    {service.serviceDurationMinutes} min
                  </span>
                ) : null}
              </div>
            </div>

            <div className="rounded-[24px] bg-soft-cream px-6 py-5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-golddeep">
                Starting Price
              </p>
              <p className="mt-2 text-3xl font-semibold text-henna md:text-4xl">
                {service?.servicePrice !== undefined &&
                service?.servicePrice !== null
                  ? formatCurrency(service.servicePrice)
                  : "Contact for pricing"}
              </p>
            </div>

            {service?.serviceDescription ? (
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-[#3b2f2f]">
                  About This Service
                </p>
                <p className="mt-3 text-base leading-8 text-muted-warm">
                  {service.serviceDescription}
                </p>
              </div>
            ) : null}

            <div className="space-y-4">
              <div className="rounded-[24px] bg-soft-cream p-6">
                <div className="flex items-center gap-2 text-henna">
                  <FiMapPin />
                  <p className="text-sm font-semibold uppercase tracking-wide">
                    Studio Appointments
                  </p>
                </div>
                <p className="mt-3 text-sm leading-7 text-muted-warm md:text-base">
                  {STUDIO_MODE_DESCRIPTION}
                </p>
              </div>

              <div className="rounded-[24px] bg-soft-cream p-6">
                <div className="flex items-center gap-2 text-henna">
                  <FiMapPin />
                  <p className="text-sm font-semibold uppercase tracking-wide">
                    Mobile Appointments
                  </p>
                </div>
                <p className="mt-3 text-sm leading-7 text-muted-warm md:text-base">
                  {MOBILE_MODE_DESCRIPTION}
                </p>
              </div>
            </div>

            {showBookingButton ? (
              <div className="pt-2">
                <Button variant="primary" size="lg" onClick={onBookNow}>
                  Book This Service
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetails;